import { useState } from "react";
import { Button, Field, Input, Panel } from "@/components/primitives";
import { signIn, twoFactor } from "@/lib/auth-client";

/**
 * Sign in: email + password, then the six-digit code (decision 48).
 *
 * Two steps rather than one screen, because Better Auth does not issue a
 * session until the second factor verifies — there is no half-signed-in state
 * to represent.
 */

type Step = "credentials" | "totp";

function messageFor(error: unknown): string {
  const code = (error as { error?: { code?: string } })?.error?.code;
  switch (code) {
    case "INVALID_EMAIL_OR_PASSWORD":
      // Deliberately one message for both: which half was wrong is not
      // something a stranger should be able to learn.
      return "That email and password do not match.";
    case "INVALID_TWO_FACTOR_AUTHENTICATION":
      return "That code is not right. Codes change every 30 seconds.";
    case "TOO_MANY_REQUESTS":
      return "Too many attempts. Wait a minute and try again.";
    default:
      return "Could not sign in. Try again, and tell the owner if it keeps happening.";
  }
}

export function LoginPage() {
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitCredentials(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await signIn.email({ email, password });
    setBusy(false);

    if (result.error) return setError(messageFor(result));

    // An enrolled account gets a challenge instead of a session; an account
    // that has never enrolled signs straight in and meets the setup gate.
    if ((result.data as { twoFactorRedirect?: boolean })?.twoFactorRedirect) {
      return setStep("totp");
    }
    window.location.replace("/");
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = useBackupCode
      ? await twoFactor.verifyBackupCode({ code })
      : await twoFactor.verifyTotp({ code });
    setBusy(false);

    if (result.error) return setError(messageFor(result));
    window.location.replace("/");
  }

  return (
    <main className="mx-auto grid min-h-dvh max-w-md place-items-center px-4 py-10">
      <div className="w-full">
        <h1 className="mb-6 text-center text-xl font-semibold">KickWin CS</h1>

        {step === "credentials" ? (
          <Panel title="Sign in">
            <form className="grid gap-4 p-5" onSubmit={submitCredentials}>
              <Field label="Email">
                <Input
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <Field label="Password">
                <Input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={busy}>
                {busy ? "Signing in…" : "Continue"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Forgotten your password? Ask the owner to reset it — there is no email sent from
                here.
              </p>
            </form>
          </Panel>
        ) : (
          <Panel
            title={useBackupCode ? "Backup code" : "Two-factor code"}
            description={
              useBackupCode
                ? "Each backup code works once."
                : "Open your authenticator app and enter the current code."
            }
          >
            <form className="grid gap-4 p-5" onSubmit={submitCode}>
              <Field label={useBackupCode ? "Backup code" : "Six-digit code"}>
                <Input
                  inputMode={useBackupCode ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.trim())}
                />
              </Field>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={busy}>
                {busy ? "Checking…" : "Sign in"}
              </Button>
              <Button
                type="button"
                variant="quiet"
                onClick={() => {
                  setUseBackupCode((on) => !on);
                  setCode("");
                  setError(null);
                }}
              >
                {useBackupCode ? "Use my authenticator app" : "I have lost my phone"}
              </Button>
            </form>
          </Panel>
        )}
      </div>
    </main>
  );
}
